<script setup lang="ts">
  import { ref, onMounted } from "vue";
  import { useRouter } from "vue-router";
  import { Text, IconLogo, Button, Spinner } from "@ui-kit";
  import { StatusBar } from "@src/components";
  import { useProfileOptions } from "@src/hooks";

  const { push } = useRouter();
  const { permissionsIsEmpty, getProfile, hasLogin, isLoading: isLoadingPermissions } = useProfileOptions();

  const timer = ref<{
    id: NodeJS.Timeout;
    time: number;
    repeat: number;
    passed: number;
  }>({ id: null, time: 10, repeat: 1, passed: 0 });

  const timerTick = () => {
    timer.value.time = timer.value.time - 1;
    timer.value.passed = timer.value.passed + 1;
  };

  const clearTimer = () => {
    clearInterval(timer.value.id);

    timer.value = {
      id: null,
      time: timer.value.repeat * 30,
      repeat: timer.value.repeat === 2 ? 2 : timer.value.repeat + 1,
      passed: 0,
    };
  };

  const refetchPermissions = async () => {
    clearTimer();
    const { isSuccess, isError } = await getProfile();

    if (isSuccess.value && !permissionsIsEmpty() && hasLogin()) {
      push("/login");
    }

    if (isError.value) {
      startTimer();
    }
  };

  const startTimer = () => {
    clearInterval(timer.value.id);

    timer.value.id = setInterval(async () => {
      if (timer.value.time > 0) {
        timerTick();
      } else {
        refetchPermissions();
      }
    }, 1000);
  };

  onMounted(() => {
    startTimer();
  });
</script>

<template>
  <div class="error-page">
    <StatusBar />
    <IconLogo class="error-page__logo" />
    <div class="error-page__content">
      <Text font="heading-H4">{{ $t("Непредвиденная ошибка") }}</Text>
      <Text font="body-regular-16">{{ $t("Проверьте соединение с интернетом и попробуйте обновить страницу") }}</Text>
    </div>
    <div class="error-page__footer">
      <Button
        class="error-page__update-btn"
        variant="secondary"
        :disabled="timer.passed < 10"
        data-testid="btn-update"
        @click="refetchPermissions"
      >
        <Spinner v-if="isLoadingPermissions" style="width: 24px; height: 24px" />
        <template v-else>{{ $t("Обновить") }} {{ timer.id ? timer.time : "" }}</template>
      </Button>
    </div>
  </div>
</template>

<style scoped>
  .error-page {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .error-page__logo {
    fill: var(--sf-base-icon-primary);
    margin: 0 auto;
    margin-top: 7px;
  }

  .error-page__content {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1;
    justify-content: center;
    align-items: center;
    text-align: center;
  }

  .error-page__footer {
    margin-top: auto;
    padding: 16px;
  }

  .error-page__update-btn {
    width: 100%;
  }
</style>
